import React, { useCallback, useEffect, useState, type JSX } from "react";

import { UploadOutlined } from "@ant-design/icons";
import { t } from "@impero-com/common/utils/i18n";
import { Button } from "@impero-com/ui";
import { Typography } from "antd";
import { cx } from "class-variance-authority";
import mime from "mime";
import moment from "moment-timezone";
import { useDropzone } from "react-dropzone";

import { useFileUploadHandler } from "./queries/useFileUploadHandler";
import * as theme from "./theme.css";
import { FileExtensionWhiteList, UploaderFile } from "./types";

const { Text } = Typography;

interface FileSelectorProps {
    setFiles: (prevFiles: (prev: UploaderFile[]) => UploaderFile[]) => void;
    preUploadCallback?: () => Promise<boolean>;
    fileExtensionWhitelist: FileExtensionWhiteList;
}
export const FileSelector = ({
    setFiles,
    preUploadCallback,
    fileExtensionWhitelist,
}: FileSelectorProps): JSX.Element => {
    const handleFileUpload = useFileUploadHandler(
        fileExtensionWhitelist,
        setFiles,
        preUploadCallback
    );

    const [focused, setFocused] = useState(false);

    const { getRootProps, getInputProps, open, isDragActive, isDragReject } =
        useDropzone({
            noClick: true,
            noKeyboard: true,
            onDrop: handleFileUpload,
        });

    const handlePaste = useCallback(
        (e: ClipboardEvent) => {
            e.stopPropagation();

            if (!focused) {
                console.debug(
                    "Paste event: not focused on file selector field"
                );
                return;
            }

            if (!e.clipboardData?.files.length) {
                console.debug("Paste event: no files pasted");
                return;
            }
            // All browsers don't actually give you more than the first file, but we still treat it as if there may be multiple files
            const blobs = Array.from(e.clipboardData.items);
            const files = blobs.reduce<File[]>((acc, blob) => {
                const date = moment();
                // We're giving all pasted files a timestamp, because pasted screenshots otherwise have a generic filename like "image"
                const formattedDate = date.format("YYYY-MM-DD HH.mm.ss.SSS");
                const fileName =
                    formattedDate + "." + mime.getExtension(blob.type);
                const file = blob.getAsFile();
                if (!file) {
                    return acc;
                }
                return [
                    ...acc,
                    new File([file], fileName, { type: blob.type }),
                ];
            }, []);

            handleFileUpload(files);
        },
        [handleFileUpload, focused]
    );

    const handleBrowseClick = (e: React.MouseEvent) => {
        e.preventDefault();
        open();
    };

    // Listens for paste event
    useEffect(() => {
        window.document.addEventListener("paste", handlePaste);
        return () => window.document.removeEventListener("paste", handlePaste);
    }, [handlePaste]);

    const dropzoneClasses = cx(
        theme.filesDropzone,
        focused && theme.focused,
        isDragActive && theme.filesDropzoneActive,
        isDragReject && theme.filesDropzoneReject
    );
    return (
        <div
            {...getRootProps({
                onBlur: () => setFocused(false),
            })}
        >
            <div
                className={dropzoneClasses}
                tabIndex={0}
                onBlur={() => setFocused(false)}
                onClick={() => setFocused(true)}
            >
                <div>
                    <input
                        {...getInputProps({
                            className: theme.focusInput,
                        })}
                    />
                    <div className={theme.dropText}>
                        <Text>{t("AddFiles_Text")}</Text>
                        <Text disabled>{t("AddFiles_Or")}</Text>
                        <div>
                            <Button
                                id={`uploader-browse-button`}
                                icon={<UploadOutlined />}
                                onClick={handleBrowseClick}
                            >
                                {t("AddFiles_LinkText")}
                            </Button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};
